import React, { useEffect, useState } from 'react';
import TextField from '@mui/material/TextField';
import StbButton from '../security-toolbox-ui/StbButton';
import StbModal from '../security-toolbox-ui/StbModal';
import { useEnrollLabAgent, useLabScenarios, useLabSessions } from '../../hooks/lab-hooks';
import {
  LabAgentEnrollment,
  LabAgentTransport,
  LAB_AGENT_TRANSPORTS,
} from '../../support/labService';
import { LabOptionSelect, labErrorMessage } from './LabShared';

export type LabAgentEnrollModalProps = {
  open: boolean;
  onClose: () => void;
};

/**
 * Enroll an agent and show its token once.
 *
 * The token is returned exactly once - the backend stores a SHA-256 digest of it and nothing
 * else, so there is no "show it again". That is why this modal changes shape after a
 * successful enrollment instead of closing: closing on success would throw away the only
 * copy of the credential the pod needs.
 *
 * The token is deliberately never put in the query cache or in the URL. It lives in this
 * component's state until the modal closes, and then it is gone.
 */
const LabAgentEnrollModal: React.FC<LabAgentEnrollModalProps> = ({ open, onClose }) => {
  const [name, setName] = useState('');
  const [transport, setTransport] = useState<LabAgentTransport>('in_pod_agent');
  const [sessionKey, setSessionKey] = useState('');
  const [scenarioSlug, setScenarioSlug] = useState('');
  const [pollInterval, setPollInterval] = useState(30);
  const [enrollment, setEnrollment] = useState<LabAgentEnrollment | null>(null);
  const [copied, setCopied] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const { data: sessions } = useLabSessions(true, { enabled: open });
  const { data: scenarios } = useLabScenarios({}, { enabled: open });
  const enroll = useEnrollLabAgent();

  useEffect(() => {
    if (open) return;

    // Drop the token the moment the modal is dismissed.
    setEnrollment(null);
    setCopied(false);
    setName('');
    setSessionKey('');
    setScenarioSlug('');
    setFormError(null);
    enroll.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSubmit = async () => {
    if (enrollment) {
      onClose();
      return;
    }

    if (!name.trim()) {
      setFormError('Give the agent a name.');
      return;
    }

    // An in-pod agent that is not bound to a session would be offered work from any pod,
    // which is the one thing the per-session scoping exists to prevent.
    if (transport === 'in_pod_agent' && !sessionKey) {
      setFormError('An in-pod agent has to be bound to a session.');
      return;
    }

    setFormError(null);

    try {
      setEnrollment(
        await enroll.mutateAsync({
          name: name.trim(),
          transport,
          ...(sessionKey ? { session_key: sessionKey } : {}),
          ...(scenarioSlug ? { scenario: scenarioSlug } : {}),
          poll_interval_seconds: pollInterval,
        }),
      );
    } catch {
      // Surfaced through enroll.error.
    }
  };

  const copy = async () => {
    if (!enrollment) return;

    try {
      await navigator.clipboard.writeText(enrollment.token);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const submitError = formError || (enroll.isError ? labErrorMessage(enroll.error) : null);

  return (
    <StbModal
      open={open}
      role="update"
      title={enrollment ? 'Agent enrolled' : 'Enroll a lab agent'}
      size="large"
      scrollable
      submitLabel={enrollment ? 'Done - I have the token' : 'Enroll'}
      hideCancel={!!enrollment}
      suppressCloseOnSubmit
      onSubmit={handleSubmit}
      onClose={async () => onClose()}
      apiResponseSection={
        submitError ? <p className="m-0 text-sm text-amber-800">{submitError}</p> : undefined
      }
    >
      {enrollment ? (
        <div className="flex flex-col gap-4">
          <div className="rounded border border-amber-400 bg-amber-50 p-3 text-sm text-amber-900">
            <div className="font-semibold">This token is shown once.</div>
            <p className="m-0 mt-1">
              Only a hash of it is kept, so it cannot be shown again. Put it on the pod&apos;s
              utility VM now; if it is lost, revoke this agent and enroll a new one.
            </p>
          </div>

          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-gray-700">Token</span>
            <textarea
              className="w-full rounded border border-gray-300 p-2 font-mono text-xs"
              rows={3}
              readOnly
              value={enrollment.token}
              onFocus={(evt) => evt.currentTarget.select()}
            />
          </label>

          <div className="flex items-center gap-2">
            <StbButton role="default" size="small" onClick={async () => copy()}>
              Copy token
            </StbButton>
            {copied && <span className="text-xs text-green-700">Copied.</span>}
          </div>

          <dl className="grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
            <div>
              <dt className="text-xs uppercase text-gray-500">Agent key</dt>
              <dd className="m-0 font-mono text-xs">{enrollment.agent_key}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase text-gray-500">Poll every</dt>
              <dd className="m-0">{`${enrollment.agent.poll_interval_seconds}s`}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase text-gray-500">Poll URL</dt>
              <dd className="m-0 font-mono text-xs">{enrollment.poll_url}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase text-gray-500">Report URL</dt>
              <dd className="m-0 font-mono text-xs">{enrollment.report_url}</dd>
            </div>
          </dl>

          <p className="m-0 text-xs text-gray-500">
            The agent fetches its device credentials itself, on its first poll. Nothing else needs
            to be configured on the pod.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <TextField
            label="Name"
            value={name}
            onChange={(evt) => setName(evt.target.value)}
            helperText="How this agent appears in the fleet list."
            fullWidth
          />

          <LabOptionSelect
            label="Transport"
            value={transport}
            options={LAB_AGENT_TRANSPORTS}
            onChange={(next) => setTransport(next)}
          />

          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-gray-700">Session</span>
            <select
              className="rounded border border-gray-300 p-2 text-sm"
              value={sessionKey}
              onChange={(evt) => setSessionKey(evt.target.value)}
            >
              <option value="">
                {transport === 'toolbox_worker'
                  ? 'Not bound to a session'
                  : 'Choose the session this agent lives in'}
              </option>
              {(sessions || []).map((session) => (
                <option value={session.session_key} key={session.session_key}>
                  {`${session.name || session.demo_name || session.session_key} (${session.session_key})`}
                </option>
              ))}
            </select>
            <span className="text-xs text-gray-500">
              An agent bound to a session is only ever offered work from that session&apos;s runs.
            </span>
          </label>

          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-gray-700">Scenario (optional)</span>
            <select
              className="rounded border border-gray-300 p-2 text-sm"
              value={scenarioSlug}
              onChange={(evt) => setScenarioSlug(evt.target.value)}
            >
              <option value="">Any scenario</option>
              {(scenarios || []).map((scenario) => (
                <option value={scenario.slug} key={scenario.slug}>
                  {scenario.name}
                </option>
              ))}
            </select>
          </label>

          <TextField
            label="Poll interval (seconds)"
            type="number"
            value={pollInterval}
            onChange={(evt) => setPollInterval(Number(evt.target.value))}
            helperText="Also how missed polls are counted, which is how the fleet view calls an agent offline."
            fullWidth
          />
        </div>
      )}
    </StbModal>
  );
};

export default LabAgentEnrollModal;
